import _ from 'lodash';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { faSync } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ApplicableTableSettings, codesCommodityList } from '../../../tableSettings';

import useFastStorage from '../../../hooks/useFastStorage';
import useFunction from '../../../hooks/useFunction';
import useGlobalOkDialog from '../../../hooks/useGlobalOkDialog';

import ApplicationContext from '../../../ApplicationContext';
import { JobNames } from '../../../framework.core/JobNames';
import { CodeCommodityListItem } from '../../../fms.data/models/CodeCommodityListItem';
import { ApiResult, InsertResult } from '../../../framework.core/data/ApiResult';
import { SQLType } from '../../../framework.core/data/SQLInfo';
import { Portal } from 'react-portal';
import TextBox from '../../../ui/TextBox';
import useSystemSettings from '../../../hooks/useSystemSettings';
import { Search } from '../../../filtering';
import EditableTable, { CellData, EditableTableHandle, TableCellTypes } from '../../../ui/tables/editing/EditableTable';
import { FmsSecTask } from '../../../fms.core/constants';
import GreenButton from '../../../ui/GreenButton';
import { checkTabIsDirty } from '../ReferenceFiles';
import { findSecurityTask } from '../../../util';

import BlackInverseButton from '../../../ui/BlackInverseButton';
import constants from '../../../constants';
import useFunctionKeys from '../../../hooks/useFunctionKeys';

function CodeRenderer({data, row, cell}:any) {
  const changeLineItem = data[0].changeLineItem;
  const l = data[row.index];
  return (
    <TextBox textId={`code-${row.index}`} value={l.code} maxLength={30} onChange={e => changeLineItem(row.index, {code: e.target.value, isUpdate:true})} />
  );
}

function DescriptionRenderer({data, row, cell}:any) {
  const changeLineItem = data[0].changeLineItem;
  const l = data[row.index];
  return (
    <TextBox textId={`description-${row.index}`} value={l.description} maxLength={100} onChange={e => changeLineItem(row.index, {description: e.target.value, isUpdate:true})} />
  );
}

function formatElement(data:any) {
  const result = new CodeCommodityListItem();
  result.id = data.id;
  result.code = data.code;
  result.description = data.description;

  return result;
}

function formatData(data: Array<any>) {
  return data.map(d => formatElement(d));
}

function CommodityCodes() {
  useFunction({invokers: {
    ifmsReferenceFilesSave: save,
    ifmsReferenceFilesCancel: cancelOperation,
    //ifmsReferenceFilesCancel: () => {},
  }});
  useFunctionKeys({ onSave: handleSaveAndClose });
  
  const appContext = useContext(ApplicationContext);
  const [showGlobalOkDialog] = useGlobalOkDialog();

  const tableRef = useRef(null);
  const masterTableRef = useRef<EditableTableHandle>(null);
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useFastStorage(`ifms-referenceFiles-haschanges`, false);
  const [data, _setData, removeData, /*dataRef*/] = useFastStorage(`ifms-referenceFiles-commoditycode-list`, []);

  const savedDataRef = useRef<Array<CodeCommodityListItem>>([]);
  const oriData = useRef<CodeCommodityListItem[]>([]);
  //const addingToTheFirstLine = useRef(false);
  
  const tableHeight = 520;
  const systemSettings = useSystemSettings();
  const listSec = findSecurityTask(appContext, FmsSecTask.CodeCommodity);
  const [focusFirstCell, setFocusFirstCell] = useState(true);

  function handleSaveAndClose() {
    const saveBtn = document.getElementById('detail-save-button');
    if (saveBtn) {
      saveBtn.focus();
      const modals = document.querySelector('[class^="ReactModal__Content"]');
      if (!modals)
        saveBtn.click();
    }
  }

  function cancelOperation() {
    setHasChanges(false);
    removeData();
    return true;
  }

  function setData(d:Array<any>, callByFetchData: boolean = false) {
    setFocusFirstCell(callByFetchData);
    _setData(d, null, { forceClone: callByFetchData });
    setHasChanges(checkTabIsDirty(CodeCommodityListItem, oriData.current, d));
  }

  async function fetchData(api:any) {
    setLoading(true);
    //<pt220811: portal
    //const apiResult = await api.getCommonCommodityCodes({orderBy: 'code', orderByAscending: true });
    //setData(formatData(apiResult && apiResult.data ? apiResult.data : []));
    const newSearch = { pageSize: systemSettings.defaultPageSize } as Search;
    const searchModel = CodeCommodityListItem.calculateSearchParameters(newSearch);
    const apiResult = await api.getListItems(JobNames.Commodity,
      'CodeCommodityListItem',
      searchModel,
      1,
      newSearch.pageSize
    );
    oriData.current = formatData(apiResult && apiResult.entities ? apiResult.entities : []);
    setData(formatData(apiResult && apiResult.entities ? apiResult.entities : []), true);
    //pt220811>

    savedDataRef.current = apiResult && apiResult.entities ? apiResult.entities : [];

    setFocusFirstCell(false);
    setLoading(false);
    setHasChanges(false);
  }

  useEffect(() => {
    if (data.length === 0)
      reload();
    else {
      oriData.current = _.cloneDeep(data);
      savedDataRef.current = _.cloneDeep(data);
    }
    return () => {
      removeData();
    };
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  function reload() {
    fetchData(appContext.api);
  }

  function getIndexOfFirstInvalidCell(rowIndex: number):number|undefined {
    if (!data) {
      return undefined;
    }

    if (!data[rowIndex].description) {
      return 1;
    }

    return undefined;
  }

  function deleteRow(index: number): boolean {
    if (!data) {
      return false;
    }

    const element = data[index];

    if (element.id > 0) {
      let deleteRow = savedDataRef.current.find(x => x.id === element.id);
      if (deleteRow) {
        deleteRow.isDelete = true;
      }
    }

    data.splice(index, 1);

    setData(data);

    return true;
  }

  function applyToLineItem(rowIndex: number, change: Partial<CodeCommodityListItem>|null) {
    if (!change || !data) {
      return;
    }
    const checkData = data[rowIndex];
    if (!change.isNew && (change.code !== checkData.code || change.description !== checkData.description)) {
      _.merge(data[rowIndex], { ..._.omit(change, ['id']), hasBeenChanged: true });
      setData(data);
      //setHasChanges(true);
    }
  }

  // function addingLineToFirstLine(){
  //   addingToTheFirstLine.current = true;
  //   masterTableRef.current?.add();
  // }

  function addLineItem() {
    const newItem = new CodeCommodityListItem();
    newItem.type = '';
    newItem.description = '';
    newItem.id = 0;
    newItem.isNew = true;

    return {
      setDataFunc: (list: any) => setData(list),
      curListData: data,
      newData: newItem
    };
  }

  async function save() {
    if (!data) {
      return;
    }

    let validData = data.filter((x:any) => x.description);
    
    let errMsg = '';
    const dataToSave = validData.filter((x:any) => x.id <= 0 || x.hasBeenChanged === true); // new data is always last so index check works
    const dataToDelete = savedDataRef.current.filter((x)=> x.isDelete);
    if(dataToSave.length === 0 && dataToDelete.length === 0) {
      if (hasChanges) reload();
      return;
    }
    setLoading(true);

    for(let i = 0; i < dataToDelete.length; ++i){
      const s = dataToDelete[i];
      const apiResult:ApiResult<InsertResult> = await appContext.api.saveListItem('CodeCommodityListItem', SQLType.Delete, _.omit(s, ['isDelete']));
      
      if(!apiResult || !apiResult.success)
        errMsg = apiResult?.message ?? constants.SAVE_ERROR_MESSAGE;
    }

    if(dataToSave.length > 0){
      const apiResult = await appContext.api.saveList('CodeCommodityListItem', dataToSave);

      if(!apiResult || !apiResult.success){
        errMsg += (errMsg ? '\n' : '' ) + apiResult?.message;
      }

      if(apiResult?.result) {
        apiResult?.result.forEach((item: any) => {
          const index = data.findIndex((d: any) => d.id === item.oldId);
          if(index >= 0){
            data[index].id = item.newId;
            data[index].hasBeenChanged = false;
          }
          // 정상 저장된 경우 isNew Flag 제거
          if(item.isNew) {
            delete data[index].isNew;
          }
        });
      }
    }

    if(errMsg) {
      await showGlobalOkDialog(<p>{errMsg}</p>);
      setLoading(false);
      return;
    }

    // setData(data);
    // savedDataRef.current = _.cloneDeep(data);
    // oriData.current = _.cloneDeep(data);
    
    // setLoading(false);
    // setHasChanges(false);
    reload();
    
    await showGlobalOkDialog(<p>The data has been saved</p>);
  }

  const tableOptions:ApplicableTableSettings = {
    customTableId: 'codes-commoditycode-list',
    ref: tableRef,
    tableName: 'commodityCodeList',
    ...codesCommodityList,
    height: `${tableHeight}px`,
    data: data.map((x:any) => ({...x, changeLineItem: applyToLineItem})),
    rowRenderers: [
      { accessor: 'indicator' },
      { accessor: 'code',  EditCell: CodeRenderer },
      { accessor: 'description',  EditCell: DescriptionRenderer },
    ],
    getCellData: (accessor: any, rowData: any, rowIndex: number): CellData | undefined => {
      if (accessor === 'code')
        return { type: TableCellTypes.Text, value: data[rowIndex].code };
      else if (accessor === 'description')
       return { type: TableCellTypes.Text, value: data[rowIndex].description };
      else
        return undefined;
    },
    isCellLock: (accessor: string, rowData: any): boolean => {
      if(!(listSec?.grantAdd || listSec?.grantEdit || listSec?.grantDel))
        return true
      else
        return false;
    },
    useEditControls: (listSec?.grantAdd || listSec?.grantEdit || listSec?.grantDel),
  };

  
  //const commodityCount = data ? data.length : 0;
  return (
    <div style={{ width: '900px', marginTop: '40px' }}>
      {/* <Portal node={document && document.getElementById('settings-placeholder')}>
        <h1>Commodity List</h1>
      </Portal> */}

      <Portal node={document && document.getElementById('settings-command-placeholder')}>
        <GreenButton size="regular" disabled={loading} onClick={save}>
          Save
        </GreenButton>
      </Portal>


      <div style={{ display: 'flex', width: '100%' }}>
        <div style={{ marginLeft: 'auto' }}></div>

        <div style={{ position: 'relative', top: '-4px' }}>
          {/* <SecondaryButton name='codes-commoditycode-add-button' size="regular" disabled={!listSec?.grantAdd} onClick={() => { addingLineToFirstLine() }}><FontAwesomeIcon icon={faPlus} />&nbsp; Add Line</SecondaryButton> &nbsp; */}
          <BlackInverseButton name='list-screen-refresh' size="large" disabled={loading} onClick={() => { reload() }} data-tooltip="Refresh" >
            <FontAwesomeIcon color='#47C83E' icon={faSync} />
          </BlackInverseButton>
        </div>
      </div>
      <div style={{ width: '864px' }}>
        <EditableTable
          initializeFocusedCell={focusFirstCell}
          ref={masterTableRef}
          tableOptions={tableOptions}
          deleteLine={deleteRow}
          addLine={addLineItem}
          getIndexOfFirstInvalidCell={getIndexOfFirstInvalidCell}
          canDeleteRow={rowNumber => listSec?.grantDel ?? false}
        />
      </div>
    </div>
  );
}

export default CommodityCodes;